import { TRANSLATIONS } from './translations';

// Every HeavyDutyMachinery enum/checklist field's allowed values, paired with
// their translation key — one source of truth so the create/edit form and
// (later) the browse-page filters render the same options with translated
// labels instead of the raw backend identifier.
export interface HeavyDutyMachineryOption {
  value: string;
  labelKey: string;
}

// Tile values are the exact boolean field names on IHeavyDutyMachinery --
// simplest possible mapping between this checklist and
// dryHireAvailable/wetHireHostOperatorAvailable/wetHireThirdPartyAvailable/
// deliveryAvailable, same trick as any other HeavyDutyMachineryForm checklist
// (see splitPredefinedAndExtra's callers) except there's no separate CSV
// column backing it -- the form maps this array to/from four booleans itself.
export const HIRE_MODE_OPTIONS: HeavyDutyMachineryOption[] = [
  { value: 'DryHireAvailable', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_HIRE_DRY },
  { value: 'WetHireHostOperatorAvailable', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_HIRE_WET_HOST },
  { value: 'WetHireThirdPartyAvailable', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_HIRE_WET_THIRD_PARTY },
  { value: 'DeliveryAvailable', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_HIRE_DELIVERY },
];

/**
 * What the wizard offers for light equipment (see
 * ITaxonomyType.isLightEquipment): the customer collects it and runs it
 * themselves, which is the only thing that happens with a pressure washer.
 *
 * The two wet-hire modes are gone because no one hires an operator to push a
 * lawnmower, and transport is gone because its own wizard step is too --
 * leaving the tile would let a host tick "con transporte" and then never be
 * asked what it costs or how far they go. A flag set with nothing behind it
 * is worse than no flag.
 */
export const HIRE_MODE_LIGHT_OPTIONS: HeavyDutyMachineryOption[] = HIRE_MODE_OPTIONS.filter(
  (o) => o.value === 'DryHireAvailable'
);

export const ACCESS_TYPE_OPTIONS: HeavyDutyMachineryOption[] = [
  { value: 'SelfCheckin', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_ACCESS_SELF_CHECKIN },
  { value: 'HostGreets', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_ACCESS_HOST_GREETS },
  { value: 'HostPresentThroughout', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_ACCESS_HOST_PRESENT_THROUGHOUT },
  { value: 'OnsiteStaff', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_ACCESS_ONSITE_STAFF },
];

// Wizard-only subset: HostPresentThroughout duplicates the wet-hire-with-
// operator concept Paso 2's hire-mode tiles already cover ("someone from the
// company is present for the rental"), and OnsiteStaff duplicates HostGreets
// ("personal de la empresa entrega en persona" already covers "personal de
// la empresa en el almacén/nave" -- both are just "company staff hands it
// over"), so the publish/edit wizard no longer offers either as a choice --
// both kept in ACCESS_TYPE_OPTIONS above (backend enum, search filters, and
// the detail page's own label lookup for any existing listing still using
// them) for compatibility.
export const ACCESS_TYPE_WIZARD_OPTIONS: HeavyDutyMachineryOption[] = ACCESS_TYPE_OPTIONS.filter(
  (o) => o.value !== 'HostPresentThroughout' && o.value !== 'OnsiteStaff'
);

export const RENTAL_MODE_OPTIONS: HeavyDutyMachineryOption[] = [
  { value: 'Hourly', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_RENTAL_HOURLY },
  { value: 'Overnight', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_RENTAL_OVERNIGHT },
  { value: 'Both', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_RENTAL_BOTH },
  { value: 'DayPass', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_RENTAL_DAY_PASS },
];

// Wizard-only subset: Day pass is retired as a choice a host can pick going
// forward -- kept in RENTAL_MODE_OPTIONS above only so the booking widget can
// still put a label on an existing DayPass listing, and because the backend
// enum still has the value.
export const RENTAL_MODE_WIZARD_OPTIONS: HeavyDutyMachineryOption[] = RENTAL_MODE_OPTIONS.filter(
  (o) => o.value !== 'DayPass'
);

/**
 * What the wizard offers for light equipment (see
 * ITaxonomyType.isLightEquipment): whole days only.
 *
 * Hourly is not missing by accident. Choosing it drags in six more required
 * fields -- buffer between rentals, minimum notice, slot granularity,
 * minimum and maximum duration, maximum days ahead -- which exist so a
 * 20-tonne machine with a transport window can be scheduled to the minute.
 * Nobody rents out a hedge trimmer by the hour, and asking them to configure
 * slot granularity to list one is how a form stops being filled in.
 *
 * "Both" goes with it: it only means "hourly AND overnight".
 */
export const RENTAL_MODE_LIGHT_OPTIONS: HeavyDutyMachineryOption[] = RENTAL_MODE_WIZARD_OPTIONS.filter(
  (o) => o.value !== 'Hourly' && o.value !== 'Both'
);

// What search offers. Two things are deliberately missing:
//
// - "Both" is a machine's capability (it supports Hourly AND Overnight), not
//   a search value on its own -- the backend has nothing to match it against.
// - "Day pass" is retired. Nobody can publish one any more (see the wizard
//   subset above), so offering it here was offering a filter that finds
//   nothing but a handful of legacy listings. It is a leftover of the
//   overnight-stay shape this platform started from, not a way anybody rents
//   a machine.
export const RENTAL_MODE_SEARCH_OPTIONS: HeavyDutyMachineryOption[] = [
  { value: 'Hourly', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_RENTAL_HOURLY },
  { value: 'Overnight', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_RENTAL_OVERNIGHT },
];

export const PARKING_OPTIONS: HeavyDutyMachineryOption[] = [
  { value: 'OnsitePrivate', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_PARKING_ONSITE_PRIVATE },
  { value: 'OnsiteDriveIn', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_PARKING_ONSITE_DRIVE_IN },
  { value: 'NearbyStreet', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_PARKING_NEARBY_STREET },
  { value: 'NearbyPaid', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_PARKING_NEARBY_PAID },
  { value: 'NoParking', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_PARKING_NONE },
];

export const CANCELLATION_POLICY_OPTIONS: HeavyDutyMachineryOption[] = [
  { value: 'Flexible', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_CANCELLATION_FLEXIBLE },
  { value: 'Moderate', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_CANCELLATION_MODERATE },
  { value: 'Strict', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_CANCELLATION_STRICT },
];

// Modest curated set matching HeavyDutyMachineryController's ValidTimezones on
// the backend — grows as new markets are added.
export const TIMEZONE_OPTIONS: HeavyDutyMachineryOption[] = [
  { value: 'Europe/Madrid', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_TIMEZONE_MADRID },
  { value: 'Atlantic/Canary', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_TIMEZONE_CANARY },
  { value: 'Europe/Lisbon', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_TIMEZONE_LISBON },
  { value: 'Europe/London', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_TIMEZONE_LONDON },
  { value: 'Europe/Paris', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_TIMEZONE_PARIS },
  { value: 'Europe/Berlin', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_TIMEZONE_BERLIN },
  { value: 'Europe/Rome', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_TIMEZONE_ROME },
  { value: 'America/Mexico_City', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_TIMEZONE_MEXICO_CITY },
  { value: 'America/Bogota', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_TIMEZONE_BOGOTA },
  { value: 'America/Argentina/Buenos_Aires', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_TIMEZONE_BUENOS_AIRES },
  { value: 'America/Santiago', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_TIMEZONE_SANTIAGO },
];
